import React, { useState, useEffect, useMemo } from "react";

/**
 * ResponsiveCardGrid - A responsive grid component that adapts to dynamic theme breakpoints
 *
 * This component solves the issue where MUI Grid doesn't respond to dynamic theme changes.
 * It manually calculates breakpoints based on current theme values and window width.
 *
 * Props:
 * - children: React nodes to render in the grid
 * - spacing: Gap between grid items (default: 2)
 * - breakpoints: Custom breakpoint configuration (optional)
 *   - xs: number of columns for extra small screens (default: 1)
 *   - sm: number of columns for small screens (default: 2)
 *   - md: number of columns for medium screens (default: 3)
 *   - lg: number of columns for large screens (optional, falls back to md)
 *
 * Usage:
 * <ResponsiveCardGrid spacing={2}>
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 *   <Card>Item 3</Card>
 * </ResponsiveCardGrid>
 *
 * Custom breakpoints:
 * <ResponsiveCardGrid breakpoints={{ xs: 1, sm: 3, md: 4, lg: 6 }}>
 *   <Card>Item 1</Card>
 * </ResponsiveCardGrid>
 */
const ResponsiveCardGrid = ({
  children,
  spacing = 2,
  breakpoints = { xs: 1, sm: 2, md: 3, lg: 4 },
  ...boxProps
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Manual breakpoint calculation using current theme values
  const getColumns = () => {
    // Default breakpoint values (MUI standard)
    const themeBreakpoints = {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    };

    if (windowWidth < themeBreakpoints.sm) {
      return breakpoints.xs;
    } else if (windowWidth < themeBreakpoints.md) {
      return breakpoints.sm || breakpoints.xs;
    } else if (windowWidth < themeBreakpoints.lg) {
      return breakpoints.md || breakpoints.sm || breakpoints.xs;
    } else {
      return (
        breakpoints.lg || breakpoints.md || breakpoints.sm || breakpoints.xs
      );
    }
  };

  const columns = getColumns();

  // Convert MUI spacing to pixels (MUI spacing unit = 8px)
  // Handle both number and responsive object (e.g., { xs: 1.5, sm: 2 })
  const gapPx = useMemo(() => {
    if (typeof spacing === "number") {
      return spacing * 8;
    } else if (typeof spacing === "object" && spacing !== null) {
      // Responsive spacing object - select based on current window width
      // Default breakpoint values (MUI standard)
      const themeBreakpoints = {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      };
      let spacingValue;
      if (windowWidth < themeBreakpoints.sm) {
        spacingValue = spacing.xs;
      } else if (windowWidth < themeBreakpoints.md) {
        spacingValue = spacing.sm;
      } else if (windowWidth < themeBreakpoints.lg) {
        spacingValue = spacing.md;
      } else {
        spacingValue = spacing.lg;
      }
      // Fallback to default if no value found
      if (spacingValue === undefined) {
        spacingValue =
          spacing.xs || spacing.sm || spacing.md || spacing.lg || 2;
      }
      return spacingValue * 8;
    }
    return 16; // default: 2 * 8
  }, [spacing, windowWidth]);
  const marginTopPx = 8; // mt: 1 = 8px

  // Convert sx prop to style if provided
  const sxStyles = React.useMemo(() => {
    if (!boxProps.sx) return {};
    const sxObj =
      typeof boxProps.sx === "function" ? boxProps.sx({}) : boxProps.sx;
    return sxObj;
  }, [boxProps.sx]);

  // Extract style and className from boxProps
  const { sx, style, className, ...restProps } = boxProps;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: `${gapPx}px`,
        marginTop: `${marginTopPx}px`,
        width: "100%",
        boxSizing: "border-box",
        ...sxStyles,
        ...style,
      }}
      className={className}
      {...restProps}
    >
      {children}
    </div>
  );
};

export default ResponsiveCardGrid;
